// Captures a poster frame from a video file for use as its thumbnail.
// Best-effort only: if the seek/decode never fires (which happens in some
// browsers when the seek target is a no-op, e.g. duration not yet available),
// a hard timeout resolves with null instead of hanging the upload forever.
export async function captureVideoThumbnail(file: File): Promise<Blob | null> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (result: Blob | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      URL.revokeObjectURL(video.src);
      video.remove();
      resolve(result);
    };

    const timeoutId = setTimeout(() => finish(null), 4000);

    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    // Safari can fail to load/seek blob: video sources that aren't attached
    // to the DOM (surfaces as WebKitBlobResource console errors) — keep it
    // off-screen but present in the document for the duration of this call.
    video.style.position = 'fixed';
    video.style.top = '-9999px';
    video.style.width = '1px';
    video.style.height = '1px';
    document.body.appendChild(video);
    video.src = URL.createObjectURL(file);

    // Safari (and some other browsers) can fire 'seeked'/'loadeddata' a beat
    // before the decoded frame is actually available to drawImage(), which
    // silently captures a solid black frame instead of throwing. A short
    // delay plus a readyState check ensures the frame has actually been
    // painted before we read pixels from it. This deliberately avoids
    // requestAnimationFrame — browsers suspend rAF entirely for backgrounded/
    // hidden tabs, which would hang this capture indefinitely (only the 4s
    // timeout would ever resolve it) if the upload happens in a tab that
    // isn't in the foreground.
    const captureFrame = (attempt = 0) => {
      setTimeout(() => {
        if (video.readyState < 2 && attempt < 5) {
          captureFrame(attempt + 1);
          return;
        }
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 320;
        canvas.height = video.videoHeight || 240;
        const ctx = canvas.getContext('2d');
        if (!ctx || canvas.width === 0 || canvas.height === 0) {
          finish(null);
          return;
        }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => finish(blob), 'image/jpeg', 0.8);
      }, 50);
    };

    video.onloadedmetadata = () => {
      // Seek slightly into the video so the thumbnail isn't a black
      // intro/fade-in frame; if duration isn't usable, just capture
      // whatever frame is already loaded (t=0) instead.
      if (!video.duration || !isFinite(video.duration)) {
        captureFrame();
        return;
      }
      video.currentTime = Math.min(0.1, video.duration);
    };
    video.onseeked = () => captureFrame();
    video.onerror = () => finish(null);
  });
}
